import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components/components.module';
import { AngularImporterModule } from './importers/angular-importer.module';
import { PrimeNGImporterModule } from './importers/prime-ng-importer.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { HttpTokenInterceptor } from './interceptors/http-token.interceptor';
import { HomeComponent } from './pages/home/home.component';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import {
  LogLevel,
  IPublicClientApplication,
  PublicClientApplication,
  BrowserCacheLocation,
  InteractionType,
} from '@azure/msal-browser';
import { SignInComponent } from './pages/sign-in/sign-in.component';
import { LOGIN_TYPE_B2C, LS_LOGIN_TYPE } from './constants/app.constant';
import { CommonService } from './services/common/common.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function MSALInstanceFactory(
  common: CommonService
): IPublicClientApplication {
  const loginType = common.getLocalStorage(LS_LOGIN_TYPE) ?? '';
  if (!!loginType && loginType === LOGIN_TYPE_B2C) {
    return new PublicClientApplication({
      auth: {
        clientId: environment.msalConfig.clientIdB2C,
        authority: environment.msalConfig.authorityUrlB2C,
        redirectUri: environment.msalConfig.redirectUriB2C,
        postLogoutRedirectUri: '/',
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
      },
      system: {
        loggerOptions: {
          loggerCallback,
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false,
        },
      },
    });
  }
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.authorityUrl,
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  // protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']); // Prod environment. Uncomment to use.
  protectedResourceMap.set(environment.msalConfig.graphUrl, ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function MSALGuardConfigFactory(
  common: CommonService
): MsalGuardConfiguration {
  const loginType = common.getLocalStorage(LS_LOGIN_TYPE) ?? '';
  if (!!loginType && loginType === LOGIN_TYPE_B2C) {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: ['user.read'],
        domainHint: 'scgchemb2c.onmicrosoft.com',
        loginHint: 'userlogin@scgchemb2c.onmicrosoft.com',
      },
      loginFailedRoute: '/login-failed',
    };
  }
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  declarations: [AppComponent, HomeComponent, SignInComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ComponentsModule,
    AngularImporterModule,
    PrimeNGImporterModule,
    BrowserAnimationsModule,
  ],
  providers: [
    MessageService,
    //============== Interceptor ==============
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTokenInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [CommonService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [CommonService],
    },
    MsalService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
